import Http from '@/services/http';
import {
  ChartQuery,
  WaterOnPaperReadings,
  WaterOnPaperStatus,
  WindingTemperatureChart,
  WaterOnPaperDryMassChart,
  WaterOnPaperParametersResponse,
  WaterOnPaperParametersRequest,
  WindingParametersResponse,
  VolumeCalculationRequest,
  VolumeCalculationResponse,
  TdgcCalculationRequest,
  TdgcCalculationResponse,
  WaterOnPaperChart,
} from 'sigma-mappings';
import { grammaticalJoin } from '@/services/arrayUtils';
import mappingFields from '@/domain/data/installation/engineering/waterOnPaper/mappingFields';
import translate, { formatNumber } from '@/services/translate';

/* istanbul ignore next */
const errors = {
  errorHandling: {
    responseMessageParams: {
      $MA025: (data) => {
        if (!data && !data.parameters) {
          return null;
        }
        const returnedField = mappingFields[data.parameters[0]];
        return {
          NomeDoCampo: translate(returnedField),
        };
      },
      $MA029: (data) => {
        if (!data && !data.parameters) {
          return null;
        }
        const returnedField = mappingFields[data.parameters[2]];
        return {
          FaixaInicial: formatNumber(data.parameters[0]),
          FaixaFinal: formatNumber(data.parameters[1]),
          NomeDoCampo: translate(returnedField),
        };
      },
      $MA039: (data) => {
        if (!data && !data.parameters) {
          return null;
        }
        const returnedField = mappingFields[data.field];
        return {
          field: translate(returnedField),
        };
      },
      $MA059: (data) => {
        if (!data && !data.parameters) {
          return null;
        }
        const returnedField = mappingFields[data.field];
        return {
          NomeDoCampo: translate(returnedField),
        };
      },
      $MA078: (data) => {
        const parameters = Object.entries(data.parameters);
        const commutator = parameters[0][1];
        const variablesArray = parameters
          .slice(1, parameters.length)
          .map(item => item[1]);
        const variables = grammaticalJoin(variablesArray);
        return {
          variables,
          commutator,
        };
      },
      $MA085: data => ({
        variable: data.parameters[0],
      }),
    },
  },
};

const urls = {
  getCards: assetModuleId => `engenharia/${assetModuleId}/agua-papel`,
  getStatus: assetModuleId => `engenharia/${assetModuleId}/agua-papel/status`,
  getWindingTemperatureChart: (assetModuleId, windingId) => `engenharia/${assetModuleId}/agua-papel/temperatura-enrolamento/${windingId}/grafico`,
  getDryMassChart: (assetModuleId, windingId) => `engenharia/${assetModuleId}/agua-papel/massa-seca/${windingId}/grafico`,
  parameters: assetModuleId => `engenharia/${assetModuleId}/agua-papel/parametros`,
  getWindingParameters: (assetModuleId, windingId) => `engenharia/${assetModuleId}/agua-papel/parametros/enrolamento/${windingId}`,
  gasesVolume: 'engenharia/agua-papel/volume-gases',
  tdgc: 'engenharia/agua-papel/tdgc',
  getChart: assetModuleId => `engenharia/${assetModuleId}/agua-papel/grafico`,
};

export default {
  getCards(assetModuleId) {
    const http = new Http(errors);
    return http
      .get(urls.getCards(assetModuleId))
      .then(({ data }) => data.map(item => new WaterOnPaperReadings(item)));
  },
  getStatus(assetModuleId) {
    const http = new Http(errors);
    return http
      .get(urls.getStatus(assetModuleId))
      .then(({ data }) => new WaterOnPaperStatus(data));
  },
  getWindingTemperatureChart(assetModuleId, windingId) {
    const http = new Http(errors);
    return http
      .get(urls.getWindingTemperatureChart(assetModuleId, windingId))
      .then(({ data }) => new WindingTemperatureChart(data));
  },
  getDryMassChart(assetModuleId, windingId) {
    const http = new Http(errors);
    return http
      .get(urls.getDryMassChart(assetModuleId, windingId))
      .then(({ data }) => new WaterOnPaperDryMassChart(data));
  },
  getParameters(assetModuleId) {
    const http = new Http(errors);
    return http
      .get(urls.parameters(assetModuleId))
      .then(({ data }) => new WaterOnPaperParametersResponse(data));
  },
  putParameters(assetModuleId, payload) {
    const http = new Http(errors);
    const mappedPayload = new WaterOnPaperParametersRequest(payload);
    return http.put(urls.parameters(assetModuleId), mappedPayload);
  },
  getWindingParameters(assetModuleId, windingId) {
    const http = new Http(errors);
    return http
      .get(urls.getWindingParameters(assetModuleId, windingId))
      .then(({ data }) => new WindingParametersResponse(data));
  },
  postGasesVolume(payload) {
    const http = new Http(errors);
    const mappedPayload = new VolumeCalculationRequest(payload);
    return http
      .post(urls.gasesVolume, mappedPayload)
      .then(({ data }) => new VolumeCalculationResponse(data));
  },
  postTdgc(payload) {
    const http = new Http(errors);
    const mappedPayload = new TdgcCalculationRequest(payload);
    return http
      .post(urls.tdgc, mappedPayload)
      .then(({ data }) => new TdgcCalculationResponse(data));
  },
  getChart(assetModuleId, chartFilters) {
    const http = new Http(errors);
    const chartQuery = new ChartQuery(chartFilters);
    return http
      .get(urls.getChart(assetModuleId), { params: chartQuery })
      .then(({ data }) => data.map(item => new WaterOnPaperChart(item)));
  },
};
